import React, { useState } from 'react';
import styled from 'styled-components';
import QRCode from 'react-qr-code';
import { useTranslation } from 'react-i18next';

/**
 * Composant pour générer et afficher des QR codes pour les liens d'affiliation
 */
const QRCodeGenerator = ({ referralCode, appLink }) => {
  const { t } = useTranslation();
  const [qrSize, setQRSize] = useState(200);
  const [qrColor, setQRColor] = useState('#504099'); // Couleur principale du thème
  const [qrBackgroundColor, setQRBackgroundColor] = useState('#ffffff');
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  
  // URL à encoder dans le QR code
  const qrValue = appLink || `https://xenya-face.app/download?ref=${referralCode}`;
  
  // Télécharger le QR code comme SVG
  const downloadQRCode = () => {
    const svg = document.getElementById('referral-qrcode');
    const svgData = new XMLSerializer().serializeToString(svg);
    const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
    const svgUrl = URL.createObjectURL(svgBlob);
    
    const downloadLink = document.createElement('a');
    downloadLink.href = svgUrl;
    downloadLink.download = `xenya-referral-${referralCode}.svg`;
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
  };
  
  // Télécharger le QR code comme PNG
  const downloadQRCodeAsPNG = () => {
    const svg = document.getElementById('referral-qrcode');
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    // Créer une image à partir du SVG
    const img = new Image();
    const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
    const svgUrl = URL.createObjectURL(svgBlob);
    
    img.onload = () => {
      // Dimensions du canvas
      canvas.width = qrSize;
      canvas.height = qrSize;
      
      // Dessiner l'image sur le canvas
      ctx.drawImage(img, 0, 0, qrSize, qrSize);
      
      // Convertir le canvas en URL data et télécharger
      const pngUrl = canvas.toDataURL('image/png');
      const downloadLink = document.createElement('a');
      downloadLink.href = pngUrl;
      downloadLink.download = `xenya-referral-${referralCode}.png`;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
      
      // Nettoyer
      URL.revokeObjectURL(svgUrl);
    };
    
    img.src = svgUrl;
  };
  
  return (
    <QRCodeContainer>
      <QRCodeHeader>
        <h3>{t('affiliate.qrcode.title')}</h3>
        <p>{t('affiliate.qrcode.description')}</p>
      </QRCodeHeader>
      
      <QRCodeWrapper>
        <QRCode
          id="referral-qrcode"
          value={qrValue}
          size={qrSize}
          level="H" // Niveau de correction d'erreur le plus élevé
          fgColor={qrColor}
          bgColor={qrBackgroundColor}
        />
      </QRCodeWrapper>
      
      <QRCodeLink>
        <LinkLabel>{t('affiliate.qrcode.link')}:</LinkLabel>
        <LinkValue>{qrValue}</LinkValue>
      </QRCodeLink>
      
      <OptionsToggle
        onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
      >
        {showAdvancedOptions 
          ? t('affiliate.qrcode.hideOptions') 
          : t('affiliate.qrcode.showOptions')}
      </OptionsToggle>
      
      {showAdvancedOptions && (
        <AdvancedOptions>
          <OptionGroup>
            <OptionLabel htmlFor="qr-size">
              {t('affiliate.qrcode.size')}:
            </OptionLabel>
            <RangeInput
              type="range"
              id="qr-size"
              min="100"
              max="400"
              step="10"
              value={qrSize}
              onChange={(e) => setQRSize(parseInt(e.target.value))}
            />
            <RangeValue>{qrSize}px</RangeValue>
          </OptionGroup>
          
          <OptionGroup>
            <OptionLabel htmlFor="qr-color">
              {t('affiliate.qrcode.color')}:
            </OptionLabel>
            <ColorInput
              type="color"
              id="qr-color"
              value={qrColor}
              onChange={(e) => setQRColor(e.target.value)}
            />
          </OptionGroup>
          
          <OptionGroup>
            <OptionLabel htmlFor="qr-bg-color">
              {t('affiliate.qrcode.bgColor')}:
            </OptionLabel>
            <ColorInput
              type="color"
              id="qr-bg-color"
              value={qrBackgroundColor}
              onChange={(e) => setQRBackgroundColor(e.target.value)}
            />
          </OptionGroup>
        </AdvancedOptions>
      )}
      
      <ButtonGroup>
        <DownloadButton onClick={downloadQRCode}>
          {t('affiliate.qrcode.downloadSVG')}
        </DownloadButton>
        <DownloadButton onClick={downloadQRCodeAsPNG}>
          {t('affiliate.qrcode.downloadPNG')}
        </DownloadButton>
      </ButtonGroup>
      
      <QRCodeTips>
        <h4>{t('affiliate.qrcode.tips.title')}</h4>
        <TipsList>
          <TipItem>{t('affiliate.qrcode.tips.tip1')}</TipItem>
          <TipItem>{t('affiliate.qrcode.tips.tip2')}</TipItem>
          <TipItem>{t('affiliate.qrcode.tips.tip3')}</TipItem>
        </TipsList>
      </QRCodeTips>
    </QRCodeContainer>
  );
};

// Styled Components
const QRCodeContainer = styled.div`
  background: white;
  border-radius: 10px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  padding: 25px;
  margin-top: 30px;
`;

const QRCodeHeader = styled.div`
  margin-bottom: 20px;
  
  h3 {
    color: #333;
    margin-bottom: 10px;
  }
  
  p {
    color: #666;
  }
`;

const QRCodeWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding: 20px;
`;

const QRCodeLink = styled.div`
  display: flex;
  align-items: center;
  background-color: #f8f9fa;
  border-radius: 5px;
  padding: 10px 15px;
  margin: 15px 0;
  overflow: hidden;
`;

const LinkLabel = styled.span`
  font-weight: 500;
  margin-right: 10px;
  white-space: nowrap;
  color :#504099
`;

const LinkValue = styled.span`
  color: #504099; /* Theme.colors.primary */
  font-size: 0.9rem;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-all;
`;

const OptionsToggle = styled.button`
  background: none;
  border: none;
  color: #504099; /* Theme.colors.primary */
  font-size: 0.9rem;
  margin-bottom: 15px;
  cursor: pointer;
  padding: 0;
  
  &:hover {
    text-decoration: underline;
  }
`;

const AdvancedOptions = styled.div`
  background-color: #f8f9fa;
  border-radius: 5px;
  padding: 15px;
  margin-bottom: 20px;
`;

const OptionGroup = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 10px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const OptionLabel = styled.label`
  font-weight: 500;
  width: 100px;
  flex-shrink: 0;
  color : #000;
`;

const RangeInput = styled.input`
  flex: 1;
`;

const RangeValue = styled.span`
  width: 60px;
  text-align: right;
  margin-left: 10px;
`;

const ColorInput = styled.input`
  width: 50px;
  height: 30px;
  border: none;
  padding: 0;
  background: none;
  cursor: pointer;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
  
  @media (max-width: 480px) {
    flex-direction: column;
  }
`;

const DownloadButton = styled.button`
  background: linear-gradient(135deg, #313866 0%, #504099 100%); /* Theme.colors.gradientPrimary */
  color: white;
  border: none;
  padding: 10px 15px;
  border-radius: 5px;
  cursor: pointer;
  transition: all 0.3s;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(80, 64, 153, 0.2);
  }
`;

const QRCodeTips = styled.div`
  h4 {
    color: #333;
    margin-bottom: 10px;
  }
`;

const TipsList = styled.ul`
  margin-left: 20px;
`;

const TipItem = styled.li`
  color: #666;
  margin-bottom: 5px;
`;

export default QRCodeGenerator;